import { useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { Pages } from '@models/Pages'
import { surveyStorage } from '@plugins/storage'
import { applyScan26Autofill } from './scan26/scan26Autofill'
import {
  saveScan26AutofillPrefs,
  loadScan26AutofillPrefs,
  SCAN26_AUTOFILL_PREFS_EVENT,
} from './scan26/scan26AutofillPrefs'
import {
  getAdminPreviewPage,
  isAdminEmbedSession,
  isAdminPreviewSession,
} from './applyAdminPreviewQuery'
import {
  isAdminPreviewMessage,
  isAllowedAdminPreviewOrigin,
  postSpaPreviewToParent,
} from './adminPreviewProtocol'

const JOURNEY_PAGE_TO_PATH: Record<string, string> = {
  consent: `/${Pages.Consent}`,
  email: `/${Pages.Survey}/${SurveyStep.Email}`,
  survey: `/${Pages.Survey}/${SurveyStep.Answer}`,
  answer: `/${Pages.Survey}/${SurveyStep.Answer}`,
  results: `/${Pages.Survey}/${SurveyStep.Results}`,
  sandbox: `/${Pages.Survey}/${SurveyStep.Sandbox}`,
  prediction: `/${Pages.Survey}/${SurveyStep.Prediction}`,
  rectify: `/${Pages.Survey}/${SurveyStep.Rectify}`,
  probability: `/${Pages.Survey}/${SurveyStep.Probability}`,
  forecasting: `/${Pages.Survey}/${SurveyStep.Forecasting}`,
}

async function captureIllustrationDataUrl(): Promise<{ dataUrl: string; width: number; height: number }> {
  const portrait = document.querySelector('#portrait') as HTMLImageElement | null
  if (!portrait || !portrait.src) {
    throw new Error('Illustration introuvable. Ouvrez une question avec visuel.')
  }

  const width = portrait.naturalWidth || portrait.clientWidth || 800
  const height = portrait.naturalHeight || portrait.clientHeight || 1200
  if (portrait.src.startsWith('data:image/')) {
    return { dataUrl: portrait.src, width, height }
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas indisponible')
  const img = new Image()
  img.crossOrigin = 'anonymous'
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('Impossible de lire l’illustration'))
    img.src = portrait.src
  })
  ctx.drawImage(img, 0, 0, width, height)
  return { dataUrl: canvas.toDataURL('image/png'), width, height }
}

/**
 * Listens for Admin 2.0 playground postMessage when session is adminPreview.
 * Mounted inside SurveyContext (DevInspectorCluster).
 */
export function AdminPreviewBridge() {
  const navigate = useNavigate()
  const { questionsList, answerObject, setQuestion, setNextStep, setAnswerObject } =
    useContext(SurveyContext)
  const stateRef = useRef({ questionsList, answerObject, setQuestion, setNextStep, setAnswerObject })
  stateRef.current = { questionsList, answerObject, setQuestion, setNextStep, setAnswerObject }

  useEffect(() => {
    if (!isAdminPreviewSession()) return

    // Notify parent that iframe is ready (studio / playground)
    postSpaPreviewToParent('ready')

    const initialPage = getAdminPreviewPage()
    if (initialPage && JOURNEY_PAGE_TO_PATH[initialPage]) {
      navigate(JOURNEY_PAGE_TO_PATH[initialPage])
      postSpaPreviewToParent('page_changed', { page: initialPage })
    }

    const onMessage = (event: MessageEvent) => {
      if (!isAllowedAdminPreviewOrigin(event.origin)) return
      if (!isAdminPreviewMessage(event.data)) return

      const { action, payload } = event.data
      const ctx = stateRef.current

      if (action === 'autofill') {
        const prev = loadScan26AutofillPrefs()
        const mode = payload?.mode === 'random' ? 'random' : 'preset'
        const scope = payload?.scope ?? 'from_zero'
        const presetId = payload?.presetId || prev.presetId
        saveScan26AutofillPrefs({
          ...prev,
          mode,
          scope,
          ...(presetId ? { presetId } : {}),
        })
        window.dispatchEvent(new Event(SCAN26_AUTOFILL_PREFS_EVENT))

        const source = mode === 'random' ? 'random' : presetId
        const result = applyScan26Autofill({
          questionsList: ctx.questionsList,
          currentAnswers: ctx.answerObject,
          source,
          scope,
        })
        ctx.setAnswerObject(result.answerObject)
        ctx.setNextStep(SurveyStep.Answer)
        navigate(`/${Pages.Survey}/${SurveyStep.Answer}`)
        if (result.focusQuestionId) {
          ctx.setQuestion(result.focusQuestionId)
        }
        postSpaPreviewToParent('autofill_done')
        return
      }

      if (action === 'capture' || action === 'capture_illustration') {
        void captureIllustrationDataUrl()
          .then(({ dataUrl, width, height }) => {
            postSpaPreviewToParent('capture_result', { dataUrl, width, height })
          })
          .catch((err: unknown) => {
            postSpaPreviewToParent('error', {
              message: err instanceof Error ? err.message : 'Capture failed',
            })
          })
        return
      }

      if (action === 'navigate' || action === 'gotoStep') {
        const page = payload?.page || payload?.step
        if (page && JOURNEY_PAGE_TO_PATH[page]) {
          navigate(JOURNEY_PAGE_TO_PATH[page])
          postSpaPreviewToParent('page_changed', { page })
          return
        }
        if (payload?.step) {
          const step = payload.step as SurveyStep
          ctx.setNextStep(step)
          navigate(`/${Pages.Survey}/${step}`)
          postSpaPreviewToParent('page_changed', { page: String(step) })
        }
        return
      }

      if (action === 'reset') {
        void surveyStorage.clear().then(() => {
          window.location.href = '/'
        })
        return
      }

      if (action === 'gotoQuestion') {
        const id = payload?.questionId
        const key = payload?.questionKey
        const q =
          (id && ctx.questionsList.find((x) => x.id === id)) ||
          (key && ctx.questionsList.find((x) => x.key === key)) ||
          null
        if (!q) return
        ctx.setNextStep(SurveyStep.Answer)
        navigate(`/${Pages.Survey}/${SurveyStep.Answer}`)
        ctx.setQuestion(q.id)
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [navigate])

  useEffect(() => {
    if (!isAdminEmbedSession()) return
    document.documentElement.classList.add('admin-embed')
    return () => document.documentElement.classList.remove('admin-embed')
  }, [])

  return null
}

export default AdminPreviewBridge
